import React, {useCallback, useEffect, useState} from 'react'
import {MBMemberInput} from '../../Atoms/MBMemberInput'
import {PasswordRuleText} from '../../Molecules/PasswordRuleText'
import {MBNgBadge, MBOkBadge, MBRequiredBadge} from '../../Atoms/Badges'
import {Spacer} from '../../../../Common/Components/Spacer'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {
  MBContentNoteWithMarkTypography,
  MBContentSubTitleTypography,
} from '../../Atoms/Typographies'
import {FlexContainer} from '../../Atoms/Containers'

const maxPasswordLength = 64

export type PasswordFormProps = {
  showRequiredBadge: boolean
  password: string
  onChange: (password: string) => void
  passwordErrors: string[]
  defaultShowPasswordChecked: boolean
  onCheckboxChange: (checked: boolean) => void
}

export const PasswordForm: React.VFC<PasswordFormProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [passwordValidState, setPasswordValidState] = useState<{ [key: string]: boolean }>({
    wordCount: false,
    useLowerCase: false,
    useUpperCase: false,
    useNumber: false,
    useSymbol: false,
  })
  const [errorMessageList, setErrorMessageList] = useState<string[]>([])
  const [is64overPassword, setIs64overPassword] = useState(false)
  const [isIncludeUnusableChars, setIsIncludeUnusableChars] = useState(false)

  useEffect(() => {
    const enteredPasswordErrorMessage: string[] = []

    if (props.password.match(/[^0-9a-zA-Z !"#$%&'()*+,./:;<=>?\\[\]^_`{|}~@-]/)) {
      setIsIncludeUnusableChars(true)
      const unusableChars = props.password.replace(
        /[0-9a-zA-Z !"#$%&'()*+,./:;<=>?\\[\]^_`{|}~@-]/g,
        '',
      )
      const deduplicatedChars = new Set(unusableChars.split(''))
      enteredPasswordErrorMessage.push(`『${Array.from(deduplicatedChars).join('')}』はパスワードに利用できません。`)
    } else {
      setIsIncludeUnusableChars(false)
    }

    if (props.password.length > maxPasswordLength) {
      setIs64overPassword(true)
      enteredPasswordErrorMessage.push(`パスワードは${maxPasswordLength}文字以内で設定ください。`)
    } else {
      setIs64overPassword(false)
    }

    setErrorMessageList([...props.passwordErrors, ...enteredPasswordErrorMessage])
  }, [props.password, props.passwordErrors])

  useEffect(() => {
    setShowPassword(props.defaultShowPasswordChecked)
  }, [props.defaultShowPasswordChecked])

  const handlePasswordValidate = useCallback((rule: string, isValid: boolean) => {
    setPasswordValidState(prevState => {
      return {
        ...prevState,
        [rule]: isValid,
      }
    })
  }, [])

  return (
    <div data-testid={'mb-password-form'}>
      <FlexContainer alignItems={'center'}>
        <label htmlFor="password-input">
          <MBContentSubTitleTypography>パスワード</MBContentSubTitleTypography>
        </label>
        {props.showRequiredBadge && (
          <Spacer ml={'6px'}>
            <FlexContainer verticalAlign={'middle'}>
              <MBRequiredBadge/>
            </FlexContainer>
          </Spacer>
        )}
      </FlexContainer>

      <Spacer mt={'9px'}>
        <MBMemberInput
          type={showPassword ? 'text' : 'password'}
          autoComplete="off"
          className="form-text_01 w_100"
          value={props.password || ''}
          id="password-input"
          placeholder=""
          hasError={props.passwordErrors.length > 0}
          onChange={(e): void => {
            props.onChange(e.target.value)
          }}
        />
      </Spacer>

      <Spacer mt={'10px'}>
        <label>
          <MBMemberInput
            className="form-check_01"
            type="checkbox"
            checked={showPassword}
            hasError={false}
            onChange={(): void => {
              setShowPassword(!showPassword)
              props.onCheckboxChange(!showPassword)
            }}
          />
          <span className="txt">パスワードを表示する</span>
        </label>
      </Spacer>

      <ErrorDisplay errors={errorMessageList}/>

      <Spacer mt={'12px'}>
        <FlexContainer alignItems={'center'}>
          <MBContentNoteWithMarkTypography note={'パスワードは下記の条件を全て満たす必要があります'}/>
          {getPasswordValidBadge(
            props.password, passwordValidState, isIncludeUnusableChars, is64overPassword,
          )}
        </FlexContainer>
        <PasswordRuleText
          ruleText="10文字以上"
          password={props.password}
          rule={/^.{10,}$/}
          onRuleValidate={(isValid): void => handlePasswordValidate('wordCount', isValid)}
        />
        <PasswordRuleText
          ruleText="英小文字(a-z)"
          password={props.password}
          rule={/^(?=.*[a-z]).*$/}
          onRuleValidate={(isValid): void => handlePasswordValidate('useLowerCase', isValid)}
        />
        <PasswordRuleText
          ruleText="英大文字(A-Z)"
          password={props.password}
          rule={/^(?=.*[A-Z]).*$/}
          onRuleValidate={(isValid): void => handlePasswordValidate('useUpperCase', isValid)}
        />
        <PasswordRuleText
          ruleText="数字(0-9)"
          password={props.password}
          rule={/^(?=.*[0-9]).*$/}
          onRuleValidate={(isValid): void => handlePasswordValidate('useNumber', isValid)}
        />
        <PasswordRuleText
          ruleText={'記号( ! " # $ % & \' ( ) * + , - . / : ; <=> ? [ \\ ] ^ _ ` { | } ~ @ )'}
          password={props.password}
          rule={/^(?=.*?[ !"#$%&'()*+,./:;<=>?\\[\]^_`{|}~@-]).*$/}
          onRuleValidate={(isValid): void => handlePasswordValidate('useSymbol', isValid)}
        />
      </Spacer>
    </div>
  )
}

const getPasswordValidBadge = (
  password: string,
  passwordValidState: { [key: string]: boolean },
  isIncludeUnusableChars: boolean,
  is64overPassword: boolean,
): JSX.Element => {
  if (password === '') {
    return <></>
  }

  const isUnavailable = Object.keys(passwordValidState).some((key) => {
    return !passwordValidState[key]
  })

  return (isUnavailable || isIncludeUnusableChars || is64overPassword) ?
    <Spacer ml={'6px'}>
      <FlexContainer verticalAlign={'middle'}>
        <MBNgBadge/>
      </FlexContainer>
    </Spacer> :
    <Spacer ml={'6px'}>
      <FlexContainer verticalAlign={'middle'}>
        <MBOkBadge/>
      </FlexContainer>
    </Spacer>
}
